"use client";

import * as React from "react";
import { cn } from "./cn";

// Pill-style toggle / tag. Used for filter chips, multi-select tag rows
// (benefits, skills), and single-pick chip groups (venue type, position).
// Driven by an explicit `selected` prop so callers manage state.

export type ChipTone = "default" | "lime" | "ember" | "info";

export type ChipProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> & {
  selected?: boolean;
  tone?: ChipTone;
  // Optional leading glyph — emoji string or icon component.
  leading?: React.ReactNode;
  size?: "sm" | "md";
};

const TONE_SELECTED: Record<ChipTone, string> = {
  default: "bg-ink text-paper border-ink",
  lime: "bg-lime text-ink border-lime",
  ember: "bg-ember/15 text-ember-dark border-ember",
  info: "bg-info/15 text-info border-info",
};

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      className,
      selected,
      tone = "default",
      leading,
      size = "md",
      children,
      disabled,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border whitespace-nowrap",
        "font-medium transition-colors select-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-1",
        size === "md" ? "px-3.5 py-1.5 text-[13px]" : "px-3 py-1 text-[12px]",
        selected
          ? TONE_SELECTED[tone]
          : "bg-paper text-ink border-smoke hover:bg-mist",
        disabled && "cursor-not-allowed opacity-50 hover:bg-paper",
        className,
      )}
      {...props}
    >
      {leading ? <span className="shrink-0">{leading}</span> : null}
      {children}
    </button>
  ),
);
Chip.displayName = "Chip";

// ────────────────────────────────────────────────────────────────────────────
// ChipGroup — single (radio-style) or multi (checkbox-style) pick.

export type ChipOption<T extends string = string> = {
  value: T;
  label: React.ReactNode;
  leading?: React.ReactNode;
  disabled?: boolean;
};

type SingleProps<T extends string> = {
  mode: "single";
  value: T | null | undefined;
  onChange: (next: T) => void;
  options: ChipOption<T>[];
  tone?: ChipTone;
  size?: "sm" | "md";
  className?: string;
};

type MultiProps<T extends string> = {
  mode: "multi";
  value: T[];
  onChange: (next: T[]) => void;
  options: ChipOption<T>[];
  tone?: ChipTone;
  size?: "sm" | "md";
  className?: string;
};

export type ChipGroupProps<T extends string = string> =
  | SingleProps<T>
  | MultiProps<T>;

export function ChipGroup<T extends string = string>(props: ChipGroupProps<T>) {
  const { tone = "default", size = "md", className } = props;
  return (
    <div
      className={cn("flex flex-wrap gap-2", className)}
      role={props.mode === "single" ? "radiogroup" : "group"}
    >
      {props.options.map((opt) => {
        const selected =
          props.mode === "single"
            ? props.value === opt.value
            : props.value.includes(opt.value);
        const handle = () => {
          if (opt.disabled) return;
          if (props.mode === "single") {
            props.onChange(opt.value);
          } else {
            const set = new Set(props.value);
            if (set.has(opt.value)) set.delete(opt.value);
            else set.add(opt.value);
            props.onChange([...set] as T[]);
          }
        };
        return (
          <Chip
            key={opt.value}
            selected={selected}
            tone={tone}
            size={size}
            leading={opt.leading}
            disabled={opt.disabled}
            onClick={handle}
            role={props.mode === "single" ? "radio" : "checkbox"}
            aria-checked={selected}
          >
            {opt.label}
          </Chip>
        );
      })}
    </div>
  );
}
